/**
  * @class meleActorDopplerBeam
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-dopplerbeam>` custom web element for showing doppler radar vehicle in beam
  */

import meleActor from 'meleActor'
const Algo = require('../../msys/msysAV/src_em/algo.js')
const Draw = require('../../msys/msysAV/src_em/draw.js')
const DopplerDraw = require('../../msys/msysAV/src_em/doppler_draw.js')

const PROPERTIES = _.extend({}, {
  ets: { description: 'event timestamp - current playback time in miliseconds', type: 'integer' },
}, meleActor._properties)

export default class meleActorDopplerBeam extends meleActor {
  init() {
    this.canvas = this.appendChildren({ tag: 'canvas' })
    this.canvas.width = 0
    this.canvas.height = 0
    this.beams = []
    super.init()
  } // init

  message({ action, params }) {
    if(params.channel===parseInt(this.parentElement.ch)) {
      if(action==='mmDopplerMeta') this.mmDopplerMeta(params)
      else if(action==='mmDopplerBeam') this.mmDopplerBeam(params)
    }
  } // message

  mmDopplerMeta(meta) {
    this.rig = Algo.rig(meta?.calibration, meta?.optics)
    if(meta.state!=='on') {
      delete this.travel_line
      Draw.clear(this.canvas)
    }
  } // mmDopplerMeta

  mmDopplerBeam(params) {
    this.beams.push(params)
    this.beams = this.beams.sort((a, b)=>b.event_dt-a.event_dt)
  } // mmDopplerBeam

  drawBeam(beam) {
    const { event_dt, channel, event } = beam
    delete this.travel_line

    const [ctx] = Draw.getContext(this.canvas, true)

    if(this.rig?.channel!==channel) {
      Draw.clear(this.canvas)
      this.drawTravelLine(ctx)
      return
    }

    const { h_pixels, v_pixels, radar_h_angle, camera_radar_side, camera_radar_forward, camera_height, project } = this.rig
    if(this.canvas.width !== h_pixels) this.canvas.width = h_pixels
    if(this.canvas.height !== v_pixels) this.canvas.height = v_pixels
    Draw.clear(ctx)

    const { lateral, speed } = event

    if(!speed) {
      this.drawTravelLine(ctx)
      return
    }

    const LAT = -(lateral + camera_radar_side)
    const p1 = project([1, LAT, -camera_height], true)
    const p2 = project([1000000, LAT, -camera_height], true)
    this.travel_line = { line: [...p1, ...p2], event_dt }
    this.drawTravelLine(ctx)

    const direction = event?.direction
    const rig = { ...this.rig }
    rig.radar_h_angle = direction==='approaching' ? radar_h_angle*2 : radar_h_angle * 1.5
    rig.camera_radar_forward = direction==='approaching' ? camera_radar_forward + 2 : camera_radar_forward
    DopplerDraw.drawBeamGroundUp(ctx, rig, lateral)
  } // drawBeam

  drawTravelLine(ctx) {
    if(this.travel_line?.line) Draw.drawLine(ctx, ...this.travel_line.line, '#FFDD00', 3)
  } // drawTravelLine

  async play() {
    this.beams = []
    delete this.travel_line
    Draw.clear(this.canvas)
  } // play

  async etsChanged(ets) {
    const keep = 50_000
    const befores = this.beams.filter(b=>b.event_dt < ets && ets-b.event_dt < keep)
    const afters = this.beams.filter(b=>b.event_dt >= ets)
    let before = befores.pop()
    const after = afters[0]
    let closest
    if(!before && !after) {
      delete this.travel_line
      Draw.clear(this.canvas)
      return
    }
    if(before && after) {
      if(Math.abs(before.event_dt-ets) < Math.abs(after.event_dt-ets)) {
        closest = before
      }else {
        closest = after
        before = null // to prevent keeping 'before beam' as 'after beam' is already closer to play time
      }
    }else {
      closest = before || after
    }

    // adjust beams stack to contain only beams that ar in future
    // and the closest beam before withing keep time
    this.beams = afters
    if(before) this.beams.unshift(before)

    // this will happen when there are some beams but too far in the future
    const diff_us = Math.abs(closest.event_dt-ets)
    if(diff_us > keep) {
      delete this.travel_line
      Draw.clear(this.canvas)
      return
    }

    this.drawBeam(closest)
  } // etsChanged
} // class meleActorDopplerBeam

meleActorDopplerBeam._properties = PROPERTIES
meleActorDopplerBeam.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-dopplerbeam', meleActorDopplerBeam)
